import { IntlShape } from "react-intl";
import { ExtraType } from "../../../../extras/types";
import { IssueFormatter } from "../../../application/hooks/useIssueFormatter";
import { ExtraCategories, ExtraCategory } from "../../../domain/ExtraCategory.enum";
import { IssueField, ValidationIssue } from "../../../domain/entities/ValidationIssue";
import { VehicleType, VehicleTypeCode } from "../../../domain/VehicleType";
import { GrpcIssues } from "../../../infrastructure/grpcIssues";
import { categoryLabelMessages, vehicleTypeMessages } from "../../labels.messages";
import { mapExtraCategoryFormToDomain } from "../../mappers/extra-category-form.mapper";
import { ExtraCategoryFormFields } from "../../types/ExtraCategoryFormTypes";

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export type ExtraCategoryField = keyof ExtraCategoryFormFields;

export interface ExtraCategoryFieldError {
  readonly field: ExtraCategoryField;
  readonly message: string;
}

/** Value of the empty *Select…* option: the form holds `undefined` (no default vehicle type). */
export const NO_VEHICLE_TYPE = "";

/** Category dropdown values, *No category* first. */
export const buildCategoryOptions = (intl: IntlShape): SelectOption[] =>
  ExtraCategories.all().map(category => ({
    value: category,
    label: intl.formatMessage(categoryLabelMessages[category]),
  }));

/** The OTA vehicle types, sorted by translated name in the user locale. */
export const buildVehicleTypeOptions = (intl: IntlShape): SelectOption[] => {
  const collator = new Intl.Collator(intl.locale);
  return VehicleType.all()
    .map(vehicleType => ({
      value: vehicleType.code,
      label: intl.formatMessage(vehicleTypeMessages[vehicleType.code]),
    }))
    .sort((a, b) => collator.compare(a.label, b.label));
};

/** Category enabled only for the two supported extra types (per unit & stay, per unit & night). */
export const isCategoryEnabled = (type: ExtraType | undefined): boolean =>
  ExtraCategories.supportsExtraType(type);

/** Vehicle fields only for a Vehicle extra. */
export const showsVehicleFields = (category: ExtraCategory | undefined): boolean =>
  category === ExtraCategory.VEHICLE;

export const parseCategoryOption = (value: string): ExtraCategory =>
  ExtraCategories.parse(value) ?? ExtraCategory.NONE;

export const parseVehicleTypeOption = (value: string): VehicleTypeCode | undefined =>
  VehicleType.tryOf(value)?.code;

/** RHF `validate` of *Vehicle type*: required for a Vehicle extra, with the server's issue text. */
export const validateVehicleType = (
  values: ExtraCategoryFormFields,
  formatter: IssueFormatter
): string | true => {
  const [issue] = mapExtraCategoryFormToDomain(values).preCheck();
  return issue ? formatter.format(issue) : true;
};

const EXTRA_FIELDS: Record<string, ExtraCategoryField> = {
  [IssueField.CATEGORY]: "category",
  [IssueField.VEHICLE_TYPE]: "vehicleType",
  [IssueField.REQUEST_LICENSE_PLATE]: "requestLicensePlate",
};

/** A vehicle field is hidden unless the category is Vehicle: its issue goes on Category. */
const fieldOf = (
  issue: ValidationIssue,
  category: ExtraCategory
): ExtraCategoryField | undefined => {
  const field = issue.target.field ? EXTRA_FIELDS[issue.target.field] : undefined;
  if (field && field !== "category" && !showsVehicleFields(category)) {
    return "category";
  }
  return field;
};

/**
 * Errors of an `INVALID_ARGUMENT` save rejection placed on the category fields. Empty
 * when the rejection carries no issue for these fields, so that the caller keeps today's
 * handling.
 */
export const extraCategoryFieldErrors = (
  issues: GrpcIssues | undefined,
  category: ExtraCategory,
  formatter: IssueFormatter
): ExtraCategoryFieldError[] => {
  if (!issues?.isInvalidArgument()) {
    return [];
  }
  return issues.issues.flatMap(issue => {
    const field = fieldOf(issue, category);
    return field ? [{ field, message: formatter.format(issue) }] : [];
  });
};
